import {
  AfterContentInit,
  ContentChildren,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  QueryList,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { OrderedMasonryItemDirective } from './ordered-masonry-item.directive';

@Directive({
  selector: '[appOrderedMasonry]',
  standalone: true,
})
export class OrderedMasonryDirective implements AfterContentInit, OnDestroy {
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef<HTMLElement>);
  private readonly masonryGap = 20;
  private layoutAnimationFrame: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private itemsChangeSubscription: Subscription | null = null;
  private hasCompletedInitialLayout = false;

  @ContentChildren(OrderedMasonryItemDirective, { descendants: true })
  private masonryItems?: QueryList<OrderedMasonryItemDirective>;

  public ngAfterContentInit(): void {
    this.setContainerStyles();
    this.itemsChangeSubscription =
      this.masonryItems?.changes.subscribe(() => {
        this.observeMasonryElements();
        this.scheduleLayout();
      }) ?? null;
    this.observeMasonryElements();
    this.scheduleLayout();
  }

  public ngOnDestroy(): void {
    if (typeof window !== 'undefined' && this.layoutAnimationFrame !== null) {
      window.cancelAnimationFrame(this.layoutAnimationFrame);
      this.layoutAnimationFrame = null;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.itemsChangeSubscription?.unsubscribe();
    this.itemsChangeSubscription = null;
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.scheduleLayout();
  }

  private setContainerStyles(): void {
    this.hostRef.nativeElement.style.position = 'relative';
    this.hostRef.nativeElement.style.visibility = 'hidden';
  }

  private observeMasonryElements(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
    this.resizeObserver.observe(this.hostRef.nativeElement);

    for (const masonryItem of this.masonryItems?.toArray() ?? []) {
      this.resizeObserver.observe(masonryItem.elementRef.nativeElement);
    }
  }

  private scheduleLayout(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.layoutAnimationFrame !== null) {
      window.cancelAnimationFrame(this.layoutAnimationFrame);
    }

    this.layoutAnimationFrame = window.requestAnimationFrame(() => {
      this.layoutAnimationFrame = null;
      this.applyLayout();
    });
  }

  private applyLayout(): void {
    const masonryContainer = this.hostRef.nativeElement;
    const masonryItemElements = (this.masonryItems?.toArray() ?? []).map(
      (masonryItem) => masonryItem.elementRef.nativeElement,
    );

    if (masonryItemElements.length === 0) {
      masonryContainer.style.height = '0px';
      masonryContainer.style.visibility = 'visible';
      this.hasCompletedInitialLayout = true;
      return;
    }

    const containerWidth =
      masonryContainer.getBoundingClientRect().width ||
      masonryContainer.parentElement?.getBoundingClientRect().width ||
      (typeof window !== 'undefined' ? window.innerWidth : 0);
    const columnCount = this.resolveColumnCount();
    const totalGapWidth = this.masonryGap * (columnCount - 1);
    const columnWidth = (containerWidth - totalGapWidth) / columnCount;

    if (columnWidth <= 0) {
      return;
    }

    const columnHeights = new Array<number>(columnCount).fill(0);

    for (const masonryItem of masonryItemElements) {
      masonryItem.style.boxSizing = 'border-box';
      masonryItem.style.position = 'absolute';
      masonryItem.style.width = `${columnWidth}px`;

      const columnIndex = this.shortestColumnIndex(columnHeights);
      const x = columnIndex * (columnWidth + this.masonryGap);
      const y = columnHeights[columnIndex];

      masonryItem.style.left = `${x}px`;
      masonryItem.style.top = `${y}px`;
      columnHeights[columnIndex] = y + masonryItem.offsetHeight + this.masonryGap;
    }

    masonryContainer.style.height = `${Math.max(...columnHeights) - this.masonryGap}px`;

    if (!this.hasCompletedInitialLayout) {
      masonryContainer.style.visibility = 'visible';
      this.hasCompletedInitialLayout = true;
    }
  }

  private resolveColumnCount(): number {
    if (typeof window === 'undefined') {
      return 1;
    }

    const viewportWidth = window.innerWidth;

    if (viewportWidth >= 1280) {
      return 4;
    }

    if (viewportWidth >= 1024) {
      return 3;
    }

    if (viewportWidth >= 640) {
      return 2;
    }

    return 1;
  }

  private shortestColumnIndex(columnHeights: number[]): number {
    let shortestIndex = 0;

    for (let columnIndex = 1; columnIndex < columnHeights.length; columnIndex += 1) {
      if (columnHeights[columnIndex] < columnHeights[shortestIndex]) {
        shortestIndex = columnIndex;
      }
    }

    return shortestIndex;
  }
}
